/**
 * Tiny procedural-synth helpers.
 *
 * Everything here is a one-shot — schedule it, forget it; the nodes
 * disconnect themselves when they finish. No retained references, no
 * leaks across the duration of a session.
 *
 * Design rules (per AESTHETIC.md §7):
 *  - Warm sines and triangles; no sawtooth, no square, no harsh transients.
 *  - All envelopes use a gentle attack (>=8 ms) and exponential decay.
 *  - exponentialRampToValueAtTime never targets 0 (illegal); we ramp to a
 *    tiny epsilon and then snap to 0 at the end.
 *
 * Public surface intentionally minimal — `cues.ts` is the only consumer.
 */

import { engine } from './engine';

const EPS = 0.0001;

export interface ToneOptions {
  freq: number;
  /** Total tail length in seconds (attack + decay). */
  duration: number;
  /** Peak gain at end of attack (0..1). Default 0.4. */
  peak?: number;
  /** Attack time in seconds. Default 0.008. */
  attack?: number;
  /** Oscillator type. Default 'sine'. */
  type?: OscillatorType;
  /** Optional detune in cents. */
  detune?: number;
  /** Optional start delay in seconds from `now()`. Default 0. */
  delay?: number;
  /** Optional destination override (otherwise routes to the cue bus). */
  destination?: AudioNode;
}

/**
 * Play a single sine-ish tone with an exp-decay envelope.
 * Returns true if scheduled; false if audio is unavailable.
 */
export function playTone(opts: ToneOptions): boolean {
  const ctx = engine.context();
  const cueBus = engine.cueBus();
  if (!ctx || !cueBus) return false;

  const dest = opts.destination ?? cueBus;
  const now = ctx.currentTime + (opts.delay ?? 0);
  const peak = opts.peak ?? 0.4;
  const attack = Math.max(0.008, opts.attack ?? 0.008);
  const duration = Math.max(attack + 0.02, opts.duration);

  const osc = ctx.createOscillator();
  osc.type = opts.type ?? 'sine';
  osc.frequency.value = opts.freq;
  if (opts.detune !== undefined) osc.detune.value = opts.detune;

  const gain = ctx.createGain();
  gain.gain.setValueAtTime(EPS, now);
  gain.gain.exponentialRampToValueAtTime(peak, now + attack);
  // Exponential decay to epsilon, then linear ramp to 0 to avoid click.
  gain.gain.exponentialRampToValueAtTime(EPS, now + duration);
  gain.gain.linearRampToValueAtTime(0, now + duration + 0.005);

  osc.connect(gain).connect(dest);
  osc.start(now);
  osc.stop(now + duration + 0.02);

  osc.onended = () => {
    try {
      osc.disconnect();
      gain.disconnect();
    } catch {
      // Already disconnected.
    }
  };

  return true;
}

/**
 * Play two tones simultaneously (a fundamental + harmonic). Convenience wrapper.
 */
export function playChord(
  base: ToneOptions,
  ...others: Array<Omit<ToneOptions, 'destination'>>
): boolean {
  const ok = playTone(base);
  if (!ok) return false;
  for (const tone of others) {
    playTone({ ...tone, destination: base.destination });
  }
  return true;
}

export interface NoiseOptions {
  /** Total tail length in seconds. */
  duration: number;
  /** Peak gain (0..1). Default 0.2. */
  peak?: number;
  /** Attack time in seconds. Default 0.015. */
  attack?: number;
  /** Optional start delay in seconds from `now()`. Default 0. */
  delay?: number;
  /** Optional filter to chain in. */
  filter?: BiquadFilterNode;
  /** Optional destination override. */
  destination?: AudioNode;
}

/**
 * Play a short pulse of white noise through an envelope.
 * If a filter is provided, the chain is noise -> filter -> gain -> destination.
 * Returns the gain node so callers can automate further if needed (used by
 * the portal whoosh's frequency sweep), or null if audio is unavailable.
 */
export function playNoise(opts: NoiseOptions): { gain: GainNode; start: number } | null {
  const ctx = engine.context();
  const cueBus = engine.cueBus();
  if (!ctx || !cueBus) return null;

  const dest = opts.destination ?? cueBus;
  const now = ctx.currentTime + (opts.delay ?? 0);
  const peak = opts.peak ?? 0.2;
  const attack = Math.max(0.01, opts.attack ?? 0.015);
  const duration = Math.max(attack + 0.02, opts.duration);

  const source = ctx.createBufferSource();
  source.buffer = makeNoiseBuffer(ctx, duration + 0.05);

  const gain = ctx.createGain();
  gain.gain.setValueAtTime(EPS, now);
  gain.gain.exponentialRampToValueAtTime(peak, now + attack);
  gain.gain.exponentialRampToValueAtTime(EPS, now + duration);
  gain.gain.linearRampToValueAtTime(0, now + duration + 0.005);

  if (opts.filter) {
    source.connect(opts.filter).connect(gain).connect(dest);
  } else {
    source.connect(gain).connect(dest);
  }

  source.start(now);
  source.stop(now + duration + 0.02);

  source.onended = () => {
    try {
      source.disconnect();
      opts.filter?.disconnect();
      gain.disconnect();
    } catch {
      // Already disconnected.
    }
  };

  return { gain, start: now };
}

/**
 * Make a white-noise buffer of the requested length (seconds).
 * Mono — stereo width is the ambient bed's job, not a cue's.
 */
export function makeNoiseBuffer(ctx: AudioContext, seconds: number): AudioBuffer {
  const length = Math.max(1, Math.floor(ctx.sampleRate * seconds));
  const buf = ctx.createBuffer(1, length, ctx.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < length; i++) {
    data[i] = Math.random() * 2 - 1;
  }
  return buf;
}

/**
 * Approximate pink-noise buffer using the Paul Kellet algorithm.
 * Pink noise is dramatically less harsh than white for wind beds.
 */
export function makePinkNoiseBuffer(ctx: AudioContext, seconds: number): AudioBuffer {
  const length = Math.max(1, Math.floor(ctx.sampleRate * seconds));
  const buf = ctx.createBuffer(1, length, ctx.sampleRate);
  const data = buf.getChannelData(0);

  let b0 = 0;
  let b1 = 0;
  let b2 = 0;
  let b3 = 0;
  let b4 = 0;
  let b5 = 0;
  let b6 = 0;

  for (let i = 0; i < length; i++) {
    const white = Math.random() * 2 - 1;
    b0 = 0.99886 * b0 + white * 0.0555179;
    b1 = 0.99332 * b1 + white * 0.0750759;
    b2 = 0.969 * b2 + white * 0.153852;
    b3 = 0.8665 * b3 + white * 0.3104856;
    b4 = 0.55 * b4 + white * 0.5329522;
    b5 = -0.7616 * b5 - white * 0.016898;
    const pink = b0 + b1 + b2 + b3 + b4 + b5 + b6 + white * 0.5362;
    b6 = white * 0.115926;
    data[i] = pink * 0.11; // normalise to roughly -1..1
  }
  return buf;
}

/**
 * Build a biquad filter on the engine's context. Returns null when audio
 * isn't unlocked yet (cues are silent in that case anyway).
 */
export function makeFilter(
  type: BiquadFilterType,
  frequency: number,
  q?: number,
): BiquadFilterNode | null {
  const ctx = engine.context();
  if (!ctx) return null;
  const filter = ctx.createBiquadFilter();
  filter.type = type;
  filter.frequency.value = frequency;
  if (q !== undefined) filter.Q.value = q;
  return filter;
}
